import { useMutation, useQuery, useSubscription } from "@apollo/client";
// import { Button } from "../../SharedComponents/Button/Button";
import gql from "graphql-tag";
import * as Cookies from "js-cookie";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { toastSuccessOptions } from "../../../App";
import getCurrentUserInformation from "../../../GraphQL/Query/getCurrentUserInformation";
import { formatPasswordChangeDate } from "../../../utils/formatPasswordChangedWhen";
import { handleGraphQLErrors } from "../../../utils/handleGraphQLError";
import { DeleteButton } from "../../SharedComponents/DeleteButton/DeleteButton";
import { DeleteModalContent } from "../../SharedComponents/DeleteModalContent/DeleteModalContent";
import { EditableInformation } from "../../SharedComponents/EditableInformation/EditableInformation";
import { EditableInformationPassword } from "../../SharedComponents/EditableInformation/EditableInformationPassword/EditableInformationPassword";
import { EditableInformationText } from "../../SharedComponents/EditableInformation/EditableInformationText/EditableInformationText";
import { EditHeader } from "../../SharedComponents/EditHeader/EditHeader";
import { Modal } from "../../SharedComponents/Modal/Modal";
import "./AdminManageUser.css";
import { AdminUserApps } from "./AdminUserApps/AdminUserApps";

export const AdminManageUser = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { t } = useTranslation();
  const id = Cookies.get("primary_account");
  const { data: currentUserData } = useQuery(getCurrentUserInformation);
  /** @type {*} */
  const params = useParams();
  const { data, error, loading } = useQuery(getUserQuery, {
    variables: { id: params.id },
    fetchPolicy: "network-only",
  });

  const [updateUser] = useMutation(updateUserInformationMutation);
  const [resendInvitation] = useMutation(resendInvitationMutation);
  const [deleteUser] = useMutation(removeUserMutation);

  const accessLevel = Cookies.get("access_level");

  const history = useHistory();

  useSubscription(subscribeToUserUpdates, {
    variables: {
      userId: params.id,
    },
    onSubscriptionData: ({ client: { cache }, subscriptionData }) =>
      updateUserOnSubData({ cache, subscriptionData, params }),
  });

  if (loading) {
    return <div>Loading...</div>;
  }
  if (error) {
    return <div>{error.message}</div>;
  }

  const canEdit =
    currentUserData?.user?.roles?.sso?.find((x) => x.accountId === id)?.role !==
    "SUPPORTER";

  const isUserDeletable = data?.user?.roles?.sso?.length === 0;

  const handleUpdateUser = (updatedData) => {
    updateUser({ variables: { id: data?.user?.id, ...updatedData } })
      .then(() => {
        toast(t("toasts.userUpdated"), toastSuccessOptions);
      })
      .catch((err) => {
        handleGraphQLErrors(err);
      });
  };

  const handleSendingResetLink = () => {
    const userId = data?.user?.id;

    resendInvitation({ variables: { userId } })
      .then(() => {
        toast(t("toasts.invitationSent"), toastSuccessOptions);
      })
      .catch((err) => {
        handleGraphQLErrors(err);
      });
  };

  return (
    <div className="UserManageUser">
      <div className="UserManageUser__content">
        {showDeleteModal && (
          <Modal
            acceptButtonColor="#AA2525"
            acceptText={t("generic.delete")}
            content={
              <DeleteModalContent
                text={t(`users.deleteUserModalBody`, {
                  name: data.user.name,
                })}
              />
            }
            title="Delete account"
            accept={() => {
              deleteUser({ variables: { id: data.user.id } })
                .then((data) => {
                  setShowDeleteModal(false);
                  history.goBack();
                })
                .catch((err) => console.error(err));
            }}
            reject={() => {
              setShowDeleteModal(false);
            }}
          />
        )}
        <div className="UserManageUser__content--inner">
          <EditHeader
            type="types.user"
            name={data?.user?.name ? data?.user?.name.trim() : data?.user.email}
            status={
              data?.user?.suspendedAt ? "states.suspended" : "states.active"
            }
            goBack={() => history.goBack()}
          />

          {data?.user?.status === "PENDING" && (
            <div
              className="UserManageUser__activation-send"
              onClick={handleSendingResetLink}
            >
              <div className="EditableInformationDisplay__action-button">
                <div className="EditableInformationDisplay__action-button-text">
                  {t("users.resendInvitation")}
                </div>
              </div>
            </div>
          )}

          <EditableInformation title="sectionTitles.information">
            <EditableInformationText
              label="generic.name"
              name="name"
              value={data?.user?.name ? data?.user?.name.trim() : "-"}
              editable={canEdit}
              onSubmit={handleUpdateUser}
            />
            <EditableInformationText
              label="generic.email"
              name="email"
              value={data?.user?.email}
              editable={canEdit}
              onSubmit={handleUpdateUser}
            />
            <EditableInformationPassword
              label="generic.password"
              value={"*********"}
              passwordChangedWhen={
                data?.user?.passwordChangedAt
                  ? formatPasswordChangeDate(data?.user?.passwordChangedAt)
                  : data?.user?.createdAt
                  ? formatPasswordChangeDate(data?.user?.createdAt)
                  : null
              }
              type="other"
              userEmail={data?.user?.email}
            />
          </EditableInformation>
          <div className="UserManageUser__content--divider"></div>
          <AdminUserApps
            userRoles={data?.user?.roles}
            userId={data?.user?.id}
            accessLevel={accessLevel}
            canEdit={canEdit}
          />
          {canEdit && (
            <div className="UserManageUser__delete-user">
              <div className="AdminDistributorEdit__delete-account--wrapper">
                <div className="AdminDistributorEdit__delete-account">
                  <DeleteButton
                    label="users.delete"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowDeleteModal(true);
                    }}
                    maxWidth="148px"
                    disabled={!isUserDeletable}
                  />
                  <p className="AdminDistributorEdit__delete-account-text">
                    {t("users.removeAccessBeforeDelete")}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const getUserQuery = gql`
  query user($id: String) {
    user(id: $id) {
      id
      name
      email
      roles
      status
      createdAt
      passwordChangedAt
    }
  }
`;

const subscribeToUserUpdates = gql`
  subscription onSSOUserRemovedFromAccount($userId: String) {
    onSSOUserUpdated(userId: $userId) {
      id
      email
      name
      roles
      passwordChangedAt
      status
    }
  }
`;

const updateUserOnSubData = ({ cache, subscriptionData, params }) => {
  const prev = cache.readQuery({
    query: getUserQuery,
    variables: { id: params.id },
  });
  const newData = {
    ...prev,
    user: {
      ...prev.user,
      roles: subscriptionData.data.onSSOUserUpdated.roles,
    },
  };
  cache.writeQuery({
    query: getUserQuery,
    variables: { id: params.id },
    data: newData,
  });
};

const updateUserInformationMutation = gql`
  mutation updateUser($id: ID!, $email: String, $name: String) {
    updateSSOUser(id: $id, email: $email, name: $name) {
      id
      name
      email
    }
  }
`;

const resendInvitationMutation = gql`
  mutation resendActivationEmail($userId: String) {
    resendActivationEmail(userId: $userId)
  }
`;

const removeUserMutation = gql`
  mutation removeSSOUser($id: ID!) {
    removeSSOUser(id: $id) {
      id
      message
    }
  }
`;
